import moment from 'moment';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import values from 'lodash/values';
import uniq from 'lodash/uniq';
import { capitalizeName } from 'Utils/label/labelUtils';
import { getCompanyOperators } from 'Components/widgets/transport-details/transportUtils';
import { getNextHourOrEndOfDay } from 'Components/widgets/additional-transports/additional-transports-filters/ArrivalDepartureRangeFilter.utils';
export const defaultState: TransportFiltersState = {
  transports: [],
  filteredTransports: [],
  stopovers: [
    {
      label: 'direct',
      min: 0,
      max: 0,
      value: '0',
      isSelected: false,
      flightIdList: [],
    },
    {
      label: 'stop.label_1',
      min: 0,
      max: 0,
      value: '1',
      isSelected: false,
      flightIdList: [],
    },
    {
      label: 'stop.label_n',
      min: 0,
      max: 0,
      value: '2',
      isSelected: false,
      flightIdList: [],
    },
  ],
  companies: [],
  connectingCities: [],
  inboundDepartureTimeRange: {
    start: moment().startOf('day'),
    end: moment().endOf('day'),
  },
  outboundDepartureTimeRange: {
    start: moment().startOf('day'),
    end: moment().endOf('day'),
  },
  durationRanges: [
    {
      label: 'label_2h',
      value: '0',
      min: 0,
      max: 120,
      isSelected: false,
      flightIdList: [],
    },
    {
      label: 'label_4h',
      value: '1',
      min: 120,
      max: 240,
      isSelected: false,
      flightIdList: [],
    },
    {
      label: 'label_8h',
      value: '2',
      min: 240,
      max: 480,
      isSelected: false,
      flightIdList: [],
    },
    {
      label: 'label_8h_more',
      value: '3',
      min: 480,
      max: 6000,
      isSelected: false,
      flightIdList: [],
    },
  ],
  totalFiltersSelected: 0,
};
export const computeDurationRangeFilters = (
  id: string,
  durationInMin: number,
  durationRanges: DurationRanges,
) => {
  if (durationInMin < 120) {
    durationRanges.firstDurationRangeIdList.push(id);
  } else if (durationInMin < 240) {
    durationRanges.secondDurationRangeIdList.push(id);
  } else if (durationInMin < 480) {
    durationRanges.thirdDurationRangeIdList.push(id);
  } else {
    durationRanges.fourthDurationRangeIdList.push(id);
  }
};
export const computeStopoverFilters = (
  id: string,
  travelSections: TravelSections,
  stopovers: Stopovers,
) => {
  if (travelSections.length === 1) {
    stopovers.transportsWithNoStopoversIdList.push(id);
  } else if (travelSections.length === 2) {
    stopovers.transportsWith1StopoversIdList.push(id);
  } else {
    stopovers.transportsWith2StopoversIdList.push(id);
  }
};
export const computeCompaniesFilters = (
  id: string,
  travelSections: TravelSections,
  companies: Companies,
) => {
  companies.push(
    ...getCompanyOperators(travelSections).map((company) => ({ ...company, flightId: id })),
  );
};
export const computeConnectingCitiesFilters = (
  id: string,
  travelSections: TravelSections,
  connectingCities: ConnectingCities,
) => {
  if (travelSections.length < 2) {
    return;
  }

  const citiesArrival = travelSections.map((travelSection) => travelSection.arrival).slice(0, -1);
  citiesArrival.forEach((cityArrival) => {
    connectingCities.push({
      label: cityArrival.label,
      cityId: cityArrival.cityId,
      flightId: id,
    });
  });
};
export const computeDepartureTimeRangesFilters = (
  travelSections: TravelSections,
  computedDepartureTimeRanges: DepartureTimeRanges,
) => {
  const departureTime = moment(travelSections[0].departure.time, 'HH:mm:ss');
  computedDepartureTimeRanges.push(departureTime);
};
export const computeTransportDetailFilters = (
  id: string,
  transportDetail: TransportDetail,
  computedFilters: {
    computedStopovers: Stopovers;
    computedDurationRange: DurationRanges;
    computedCompanies: Companies;
    computedConnectingCities: ConnectingCities;
    computedDepartureTimeRanges: DepartureTimeRanges;
  },
) => {
  const {
    computedStopovers,
    computedDurationRange,
    computedCompanies,
    computedConnectingCities,
    computedDepartureTimeRanges,
  } = computedFilters;
  computeStopoverFilters(id, transportDetail.travelSections, computedStopovers);
  computeDurationRangeFilters(id, Number(transportDetail.durationInMin), computedDurationRange);
  computeCompaniesFilters(id, transportDetail.travelSections, computedCompanies);
  computeConnectingCitiesFilters(id, transportDetail.travelSections, computedConnectingCities);
  computeDepartureTimeRangesFilters(transportDetail.travelSections, computedDepartureTimeRanges);
};
export const computeFilters = (
  computedStopovers: Stopovers,
  computedDurationRange: DurationRanges,
  computedCompanies: Companies,
  computedConnectingCities: ConnectingCities,
  computedInboundDepartureTimeRanges: DepartureTimeRanges,
  computedOutboundDepartureTimeRanges: DepartureTimeRanges,
) => {
  const stopovers: Array<TransportFilter> = values(computedStopovers).map(
    (stopoverIdList: Array<string>, index) => ({
      ...defaultState.stopovers[index],
      flightIdList: uniq(stopoverIdList),
    }),
  );
  const durationRanges: Array<TransportFilter> = values(computedDurationRange).map(
    (durationRangesIdList: Array<string>, index) => ({
      ...defaultState.durationRanges[index],
      flightIdList: uniq(durationRangesIdList),
    }),
  );
  const groupedCompanies = groupBy(computedCompanies, 'id');
  const companies: Array<TransportFilter> = sortBy(
    Object.keys(groupedCompanies).map((companyId) => ({
      value: groupedCompanies[companyId][0].id,
      label: capitalizeName(groupedCompanies[companyId][0].label),
      flightIdList: uniq(groupedCompanies[companyId].map((company) => company.flightId)),
      isSelected: false,
      min: 0,
      max: 0,
    })),
    'label',
  );
  const groupedConnectingCities = groupBy(computedConnectingCities, 'cityId');
  const connectingCities: Array<TransportFilter> = sortBy(
    Object.keys(groupedConnectingCities).map((connectingCityId) => ({
      value: groupedConnectingCities[connectingCityId][0].cityId,
      label: capitalizeName(groupedConnectingCities[connectingCityId][0].label),
      flightIdList: uniq(
        groupedConnectingCities[connectingCityId].map((connectingCity) => connectingCity.flightId),
      ),
      isSelected: false,
      min: 0,
      max: 0,
    })),
    'label',
  );
  const inboundDepartureTimeRange = {
    start: moment.min(computedInboundDepartureTimeRanges).startOf('hour'),
    end: moment.max(computedInboundDepartureTimeRanges).startOf('hour'),
  };
  const outboundDepartureTimeRange = {
    start: moment.min(computedOutboundDepartureTimeRanges).startOf('hour'),
    end: moment.max(computedOutboundDepartureTimeRanges).startOf('hour'),
  };
  return {
    stopovers,
    durationRanges,
    companies,
    connectingCities,
    inboundDepartureTimeRange,
    outboundDepartureTimeRange,
  };
};
export const applyFilters = (state: TransportFiltersState) => {
  const {
    transports,
    stopovers,
    durationRanges,
    companies,
    connectingCities,
    inboundDepartureTimeRange,
    outboundDepartureTimeRange,
  } = state;
  const totalFiltersSelected =
    stopovers.filter((stopover) => stopover.isSelected).length +
    durationRanges.filter((durationRange) => durationRange.isSelected).length +
    companies.filter((company) => company.isSelected).length +
    connectingCities.filter((connectingCity) => connectingCity.isSelected).length;
  const filteredTransports: Array<AlternativeTransport> = transports.filter((transport) => {
    const { id, inbound, outbound } = transport;
    const inboundStopovers = inbound.travelSections.length - 1;
    const outboundStopovers = outbound.travelSections.length - 1;
    const inboundDurationInMin = Number(inbound.durationInMin);
    const outboundDurationInMin = Number(outbound.durationInMin);
    const inboundCompanyIds = getCompanyOperators(inbound.travelSections).map(
      (company) => company.id,
    );
    const outboundCompanyIds = getCompanyOperators(outbound.travelSections).map(
      (company) => company.id,
    );
    const inboundConnectingCityIds = inbound.travelSections
      .map((travelSection) => travelSection.arrival.cityId)
      .slice(0, -1);
    const outboundConnectingCityIds = outbound.travelSections
      .map((travelSection) => travelSection.arrival.cityId)
      .slice(0, -1);
    // add rule for all filters to check if none is selected
    const allStopoversNotSelected = stopovers.every((stopover) => !stopover.isSelected);
    const allDurationRangesNotSelected = durationRanges.every(
      (durationRange) => !durationRange.isSelected,
    );
    const allCompaniesNotSelected = companies.every((company) => !company.isSelected);
    const allConnectingCitiesNotSelected = connectingCities.every(
      (connectingCity) => !connectingCity.isSelected,
    );
    const isInboundStopoverSelected = stopovers.some(
      (stopover) =>
        stopover.isSelected &&
        stopover.flightIdList.includes(id) &&
        Number(stopover.value) <= inboundStopovers,
    );
    const isOutboundStopoverSelected = stopovers.some(
      (stopover) =>
        stopover.isSelected &&
        stopover.flightIdList.includes(id) &&
        Number(stopover.value) <= outboundStopovers,
    );
    const isInboundDurationRangeSelected = durationRanges.some(
      (durationRange) =>
        durationRange.isSelected &&
        durationRange.flightIdList.includes(id) &&
        durationRange.min <= inboundDurationInMin &&
        durationRange.max > inboundDurationInMin,
    );
    const isOutboundDurationRangeSelected = durationRanges.some(
      (durationRange) =>
        durationRange.isSelected &&
        durationRange.flightIdList.includes(id) &&
        durationRange.min <= outboundDurationInMin &&
        durationRange.max > outboundDurationInMin,
    );
    const isInboundCompanySelected = companies.some(
      (company) =>
        company.isSelected &&
        company.flightIdList.includes(id) &&
        inboundCompanyIds.includes(company.value),
    );
    const isOutboundCompanySelected = companies.some(
      (company) =>
        company.isSelected &&
        company.flightIdList.includes(id) &&
        outboundCompanyIds.includes(company.value),
    );
    const isInboundConnectingCitySelected = connectingCities.some(
      (connectingCity) =>
        connectingCity.isSelected &&
        connectingCity.flightIdList.includes(id) &&
        inboundConnectingCityIds.includes(connectingCity.value),
    );
    const isOutboundConnectingCitySelected = connectingCities.some(
      (connectingCity) =>
        connectingCity.isSelected &&
        connectingCity.flightIdList.includes(id) &&
        outboundConnectingCityIds.includes(connectingCity.value),
    );
    const isInboundDepartureTimeInRange = moment(
      inbound.travelSections[0].departure.time,
      'HH:mm:ss',
    ).isBetween(inboundDepartureTimeRange.start, inboundDepartureTimeRange.end, 'minute', '[]');
    const isOutboundDepartureTimeInRange = moment(
      outbound.travelSections[0].departure.time,
      'HH:mm:ss',
    ).isBetween(outboundDepartureTimeRange.start, outboundDepartureTimeRange.end, 'minute', '[]');
    return (
      (isInboundStopoverSelected || isOutboundStopoverSelected || allStopoversNotSelected) &&
      (isInboundDurationRangeSelected ||
        isOutboundDurationRangeSelected ||
        allDurationRangesNotSelected) &&
      (isInboundCompanySelected || isOutboundCompanySelected || allCompaniesNotSelected) &&
      (isInboundConnectingCitySelected ||
        isOutboundConnectingCitySelected ||
        allConnectingCitiesNotSelected) &&
      isInboundDepartureTimeInRange &&
      isOutboundDepartureTimeInRange
    );
  });
  return {
    filteredTransports,
    totalFiltersSelected,
  };
};
export const initReducer = (
  state: TransportFiltersState,
  transports: Array<AlternativeTransport>,
) => {
  const computedStopovers: Stopovers = {
    transportsWithNoStopoversIdList: [],
    transportsWith1StopoversIdList: [],
    transportsWith2StopoversIdList: [],
  };
  const computedDurationRange: DurationRanges = {
    firstDurationRangeIdList: [],
    secondDurationRangeIdList: [],
    thirdDurationRangeIdList: [],
    fourthDurationRangeIdList: [],
  };
  const computedCompanies: Companies = [];
  const computedConnectingCities: ConnectingCities = [];
  const computedInboundDepartureTimeRanges: DepartureTimeRanges = [];
  const computedOutboundDepartureTimeRanges: DepartureTimeRanges = [];

  if (!transports) {
    return state;
  }

  transports.forEach((transport) => {
    const { id, inbound, outbound } = transport;
    const computedFilters = {
      computedStopovers,
      computedDurationRange,
      computedCompanies,
      computedConnectingCities,
    };
    computeTransportDetailFilters(id, inbound, {
      ...computedFilters,
      computedDepartureTimeRanges: computedInboundDepartureTimeRanges,
    });
    computeTransportDetailFilters(id, outbound, {
      ...computedFilters,
      computedDepartureTimeRanges: computedOutboundDepartureTimeRanges,
    });
  });
  const {
    stopovers,
    durationRanges,
    companies,
    connectingCities,
    inboundDepartureTimeRange,
    outboundDepartureTimeRange,
  } = computeFilters(
    computedStopovers,
    computedDurationRange,
    computedCompanies,
    computedConnectingCities,
    computedInboundDepartureTimeRanges,
    computedOutboundDepartureTimeRanges,
  );
  return {
    ...state,
    stopovers,
    durationRanges,
    companies,
    connectingCities,
    inboundDepartureTimeRange: {
      ...inboundDepartureTimeRange,
      end: getNextHourOrEndOfDay(inboundDepartureTimeRange.end),
    },
    outboundDepartureTimeRange: {
      ...outboundDepartureTimeRange,
      end: getNextHourOrEndOfDay(outboundDepartureTimeRange.end),
    },
    defaultInboundDepartureTimeRange: inboundDepartureTimeRange,
    defaultOutboundDepartureTimeRange: outboundDepartureTimeRange,
    transports: transports,
    filteredTransports: transports,
  };
};
